"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardDescription, CardTitle } from "@/components/ui/card";
import type { KeywordKeywordsProps } from "../../types/keyword.types";
import { generateKeywordsQuery } from "@/features/keyword-monitoring/utils/stepperUtils";
import { useKeywordsStore } from "@/stores/keywordsStore";
import { toast } from "sonner";

export function KeywordKeywords({
  onKeywordsChange,
  enableTitle = true,
}: KeywordKeywordsProps) {
  const keywords = useKeywordsStore((state) => state.keywords);
  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const [mainInput, setMainInput] = React.useState("");
  const [additionalInput, setAdditionalInput] = React.useState("");
  const [excludedInput, setExcludedInput] = React.useState("");
  const prevQuery = React.useRef<string>("");

  // Helper function to check for duplicate keywords (case-insensitive)
  const checkDuplicateKeyword = (newKeyword: string): string | null => {
    const lowerNewKeyword = newKeyword.toLowerCase();

    if (keywords.main.some((k) => k.toLowerCase() === lowerNewKeyword)) {
      return "main";
    }
    if (keywords.additional.some((k) => k.toLowerCase() === lowerNewKeyword)) {
      return "additional";
    }
    if (keywords.excluded.some((k) => k.toLowerCase() === lowerNewKeyword)) {
      return "excluded";
    }

    return null;
  };

  // Update parent component with keywords query
  React.useEffect(() => {
    if (onKeywordsChange) {
      const query = generateKeywordsQuery(
        keywords.main,
        keywords.additional,
        keywords.excluded,
      );
      if (query !== prevQuery.current) {
        prevQuery.current = query;
        onKeywordsChange(query);
      }
    }
  }, [keywords.main, keywords.additional, keywords.excluded, onKeywordsChange]);

  return (
    <div className="flex flex-col gap-4 pb-16">
      <div className="flex-shrink-0">
        <div className="flex w-full flex-row items-center gap-4">
          {enableTitle && (
            <div className="mb-4 min-w-0 flex-1">
              <CardTitle className="leading-normal">Keywords</CardTitle>
              <CardDescription>
                Separate multiple keywords with commas
              </CardDescription>
            </div>
          )}
          <div className="flex flex-wrap gap-2 sm:flex-nowrap">
            <Button
              onClick={() => {
                setKeywords({ main: [], additional: [], excluded: [] });
                setMainInput("");
                setAdditionalInput("");
                setExcludedInput("");
              }}
              variant="destructive"
            >
              Clear All
            </Button>
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="overflow-y grid h-full flex-1 grid-rows-3 gap-16">
            {/* Main Keywords Column */}
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex flex-shrink-0 flex-col gap-2">
                <h3 className="text-md font-bold text-neutral-800">
                  Main Keywords
                </h3>
                <Input
                  placeholder={`Enter your main keywords here...`}
                  value={mainInput}
                  onChange={(e) => setMainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const trimmed = mainInput.trim();
                      if (trimmed) {
                        const duplicateCategory =
                          checkDuplicateKeyword(trimmed);
                        if (duplicateCategory) {
                          toast.info(
                            `"${trimmed}" is already in ${duplicateCategory} keywords`,
                          );
                        }
                        setKeywords({
                          ...keywords,
                          main: [...keywords.main, trimmed],
                        });
                        setMainInput("");
                      }
                    }
                  }}
                  className="mb-2 rounded-none py-2"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {keywords.main.map((keyword) => (
                    <Badge
                      key={keyword}
                      variant="main"
                      onRemove={() =>
                        setKeywords({
                          ...keywords,
                          main: keywords.main.filter((k) => k !== keyword),
                        })
                      }
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Keywords Column */}
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex flex-shrink-0 flex-col gap-2">
                <h3 className="text-md font-bold text-neutral-800">
                  Additional Keywords
                </h3>
                <Input
                  variant="default"
                  placeholder={
                    !keywords.main.length
                      ? "Enter main keywords first..."
                      : "Enter your additional keywords here..."
                  }
                  disabled={!keywords.main.length}
                  value={additionalInput}
                  onChange={(e) => setAdditionalInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const trimmed = additionalInput.trim();
                      if (trimmed) {
                        const duplicateCategory =
                          checkDuplicateKeyword(trimmed);
                        if (duplicateCategory) {
                          toast.info(
                            `"${trimmed}" is already in ${duplicateCategory} keywords`,
                          );
                        }
                        setKeywords({
                          ...keywords,
                          additional: [...keywords.additional, trimmed],
                        });
                        setAdditionalInput("");
                      }
                    }
                  }}
                  className="mb-2 rounded-none py-2"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {keywords.additional.map((keyword) => (
                    <Badge
                      key={keyword}
                      variant="additional"
                      onRemove={() =>
                        setKeywords({
                          ...keywords,
                          additional: keywords.additional.filter(
                            (k) => k !== keyword,
                          ),
                        })
                      }
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Excluded Keywords Column */}
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex flex-shrink-0 flex-col gap-2">
                <h3 className="text-md font-bold text-neutral-800">
                  Excluded Keywords
                </h3>
                <Input
                  placeholder="Enter your excluded keywords here..."
                  value={excludedInput}
                  onChange={(e) => setExcludedInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const trimmed = excludedInput.trim();
                      if (trimmed) {
                        const duplicateCategory =
                          checkDuplicateKeyword(trimmed);
                        if (duplicateCategory) {
                          toast.info(
                            `"${trimmed}" is already in ${duplicateCategory} keywords`,
                          );
                        }
                        setKeywords({
                          ...keywords,
                          excluded: [...keywords.excluded, trimmed],
                        });
                        setExcludedInput("");
                      }
                    }
                  }}
                  className="mb-2 rounded-none py-2"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {keywords.excluded.map((keyword) => (
                    <Badge
                      key={keyword}
                      variant="excluded"
                      onRemove={() =>
                        setKeywords({
                          ...keywords,
                          excluded: keywords.excluded.filter(
                            (k) => k !== keyword,
                          ),
                        })
                      }
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="flex gap-2 justify-end mt-6 flex-shrink-0 w-full">
          <div className="flex gap-2 justify-end w-full">
            <Button variant="outline" onClick={onBack} className="w-1/2">
              Back
            </Button>
          </div>
        </div> */}
      </div>
    </div>
  );
}
